import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { CreditCard, Moon, Sun, Settings, WifiOff, PenLine } from "lucide-react";
import { UserButton } from "@clerk/clerk-react";
import { NAV_ITEMS } from "@/constants/navigation";
import { useTheme } from "@/context/ThemeContext";

export default function DashboardNav() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { theme, toggleTheme } = useTheme();
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);

    const check = async () => {
      try {
        await fetch(`${import.meta.env.VITE_BACKEND_URL || "http://localhost:8080/"}api/health`, {
          method: "HEAD", cache: "no-store", signal: AbortSignal.timeout(3000),
        });
        setIsOnline(true);
      } catch {
        setIsOnline(false);
      }
    };

    const interval = setInterval(check, 10000);
    return () => {
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!showSettings) return;
    const close = (e) => {
      if (!e.target.closest("[data-topnav-settings]")) setShowSettings(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showSettings]);


  return (
    <header className="sticky top-0 z-40 h-14 bg-brand-800 text-white/75 flex items-center px-4 border-b border-white/[0.08] shrink-0">
      {/*
        Scoped container-query fallback. Tailwind 3.4 needs the
        @tailwindcss/container-queries plugin for `@container` utilities, and
        that plugin is not installed. We need a *container* query (not a media
        query) here because the topnav-links width depends on its siblings
        (brand + right cluster), not the viewport. Keeping the rule local to
        this component keeps global.css free of recipe styles.
      */}
      <style>{`
        [data-topnav-links] { container-type: inline-size; }
        @container (max-width: 530px) {
          [data-topnav-link-label] { display: none; }
          [data-topnav-link] { padding: 0.4rem 0.55rem; }
        }
      `}</style>

      {/* Brand */}
      <Link
        to="/dashboard"
        className="flex items-center gap-2 font-display text-[1.05rem] text-white no-underline whitespace-nowrap shrink-0 mr-6"
      >
        <img
          src="/images/easyreports_logo.png"
          alt="EasyReports logo"
          style={{ height: 20, width: 20, objectFit: "contain", flexShrink: 0 }}
        />
        <span>EasyReports</span>
      </Link>

      {/* Nav links - hidden on mobile (bottom nav takes over) */}
      <nav data-topnav-links className="flex items-center justify-center gap-1 flex-1 max-md:hidden">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/dashboard"}
            data-topnav-link
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-3 py-[6px] rounded-full text-sm font-medium no-underline whitespace-nowrap transition-colors ${
                isActive
                  ? "bg-brand-600 text-white"
                  : "text-white/[0.62] hover:bg-white/[0.09] hover:text-white/90"
              }`
            }
          >
            <item.icon size={16} />
            <span data-topnav-link-label>{item.name}</span>
          </NavLink>
        ))}
      </nav>

      {/* Right cluster */}
      <div className="flex items-center gap-3 ml-auto shrink-0">
        {!isOnline && (
          <div className="flex items-center gap-1 text-xs font-medium px-2 py-[0.2rem] rounded-md bg-destructive-bg text-destructive-fg border border-destructive-border">
            <WifiOff size={13} className="shrink-0" />
            <span>Offline</span>
          </div>
        )}

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
          title={theme === "light" ? "Dark mode" : "Light mode"}
          className="w-7 h-7 rounded-[6px] flex items-center justify-center border-none cursor-pointer transition-colors text-white/65 bg-white/[0.08] hover:bg-white/[0.18]"
        >
          {theme === "light" ? <Moon size={15} /> : <Sun size={15} />}
        </button>

        {/* Settings icon + popover */}
        <div data-topnav-settings className="relative">
          <button
            className={`w-7 h-7 rounded-[6px] flex items-center justify-center border-none cursor-pointer transition-colors text-white/65 ${
              showSettings ? "bg-white/[0.18]" : "bg-white/[0.08] hover:bg-white/[0.18]"
            }`}
            onClick={() => setShowSettings((s) => !s)}
            title="Settings"
          >
            <Settings size={15} />
          </button>

          {showSettings && (
            <div className="absolute top-[calc(100%+8px)] right-0 bg-brand-900 border border-white/10 rounded-xl p-2 z-50 min-w-[160px] shadow-[0_8px_24px_rgba(0,0,0,0.3)]">
              <Link
                to="/credits-and-billing"
                onClick={() => setShowSettings(false)}
                className="flex items-center gap-2 px-[10px] py-2 rounded-lg text-white/75 text-[13px] no-underline transition-colors hover:bg-white/[0.08]"
              >
                <CreditCard size={14} /> Credits & Billing
              </Link>
              <Link
                to="/settings/signature"
                onClick={() => setShowSettings(false)}
                className="flex items-center gap-2 px-[10px] py-2 rounded-lg text-white/75 text-[13px] no-underline transition-colors hover:bg-white/[0.08]"
              >
                <PenLine size={14} /> My Signature
              </Link>
            </div>
          )}
        </div>

        <UserButton afterSignOutUrl="/" />
      </div>
    </header>
  );
}
